import { useState, type FormEvent } from 'react';
import {
  useCreateCeoDebtInstrumentMutation,
  useGetCeoDebtsPortfolioQuery,
  useMarkCeoDebtSchedulePaidMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { DebtDetailModal } from '../DebtDetailModal';
import { TreasuryViewNote, useTreasuryWrite } from '../treasuryAccess';
import { HeaderActions } from '../ui';
import { AddDebtModal } from './AddDebtModal';
import { DebtsCalendarGrid } from './DebtsCalendarGrid';
import { DebtsImportDialog } from './DebtsImportDialog';
import { DebtsKpiStrip } from './DebtsKpiStrip';
import { DebtInstrumentsTable } from './DebtInstrumentsTable';
import {
  ErrorRow,
  HeaderRow,
  RatesSubtext,
  Section,
  Select,
  Title,
} from './styles';
import type { DebtsPageProps } from './types';
import { UpcomingScheduleTable } from './UpcomingScheduleTable';

export function CeoDebtsView({ editable = false }: DebtsPageProps) {
  const can_write = useTreasuryWrite(editable);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const { data, isLoading, isError, refetch } = useGetCeoDebtsPortfolioQuery(
    typeFilter ? { instrument_type: typeFilter } : undefined,
  );
  const [createDebt, { isLoading: isCreating }] = useCreateCeoDebtInstrumentMutation();
  const [markPaid] = useMarkCeoDebtSchedulePaidMutation();

  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Поля нового кредита / лизинга
  const [instType, setInstType] = useState('credit_line_vkl');
  const [contractNo, setContractNo] = useState('');
  const [title, setTitle] = useState('');
  const [creditor, setCreditor] = useState('');
  const [totalLimit, setTotalLimit] = useState('');
  const [principalBalance, setPrincipalBalance] = useState('');
  const [rateType, setRateType] = useState('floating_cbr');
  const [baseRate, setBaseRate] = useState('19');
  const [marginRate, setMarginRate] = useState('2.5');
  const [startDate, setStartDate] = useState('2026-01-15');
  const [endDate, setEndDate] = useState('2027-01-15');
  const [leasedAssetName, setLeasedAssetName] = useState('');
  const [leasedAssetCount, setLeasedAssetCount] = useState('1');
  const [collateral, setCollateral] = useState('');
  const [covenants, setCovenants] = useState('');
  const [autoGenerateSchedule, setAutoGenerateSchedule] = useState(true);

  if (isLoading) {
    return <Card>Загрузка портфеля и календаря кредитов и лизингов…</Card>;
  }

  if (isError || !data) {
    return (
      <Card>
        <ErrorRow>
          <span>Не удалось загрузить портфель кредитов и лизингов.</span>
          <Button $size="sm" $variant="secondary" onClick={() => refetch()}>
            Повторить
          </Button>
        </ErrorRow>
      </Card>
    );
  }

  async function handleCreateDebt(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || !creditor.trim() || !contractNo.trim()) return;
    const lim = Number(totalLimit) || 0;
    const isGuarantee = instType === 'bank_guarantee';
    const parsedBal = Number(principalBalance);
    const bal =
      principalBalance.trim() !== '' && Number.isFinite(parsedBal)
        ? parsedBal
        : isGuarantee
          ? 0
          : lim;
    const drawn = isGuarantee ? lim : bal;
    try {
      setErrorMsg(null);
      const created = await createDebt({
        instrument_type: instType,
        contract_number: contractNo.trim(),
        title: title.trim(),
        creditor_name: creditor.trim(),
        total_limit: lim,
        drawn_amount: drawn,
        principal_balance: bal,
        rate_type: rateType,
        base_rate_pct: Number(baseRate) || (isGuarantee ? 2.2 : 18),
        margin_pct: Number(marginRate) || 0,
        start_date: startDate,
        end_date: endDate,
        leased_asset_name: instType === 'leasing' ? leasedAssetName.trim() : undefined,
        leased_asset_count: instType === 'leasing' ? Number(leasedAssetCount) || 1 : 0,
        collateral_summary: collateral.trim() || undefined,
        covenants_summary: covenants.trim() || undefined,
        auto_generate_schedule: autoGenerateSchedule,
      }).unwrap();
      setShowAddModal(false);
      setSelectedInstrumentId(created.instrument.id);
    } catch {
      setErrorMsg('Не удалось создать договор. Проверьте корректность полей и повторите.');
    }
  }

  return (
    <>
      {!editable && <TreasuryViewNote section="Кредиты и лизинг" to="/agents/finance/debts" />}
      <Section>
        <HeaderRow>
          <div>
            <Title>Календарь и портфель кредитов, ВКЛ/НКЛ, лизингов и банковских гарантий</Title>
            <RatesSubtext>
              Ключевая ставка ЦБ РФ: <strong>{data.cbr_key_rate_pct.toFixed(1)}%</strong> ·
              Средневзвешенная ставка портфеля:{' '}
              <strong>{data.weighted_avg_rate_pct.toFixed(2)}%</strong> · Покрытие выплат 30 дн.
              свободным остатком: <strong>{data.cash_coverage_ratio_30d.toFixed(1)}x</strong>
            </RatesSubtext>
          </div>

          <HeaderActions>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Все инструменты (Кредиты + Лизинг + БГ)</option>
              <option value="credits">Только кредиты и кредитные линии (ВКЛ/НКЛ)</option>
              <option value="leasing">Только лизинг спецтехники и автотранспорта</option>
              <option value="bank_guarantee">Только банковские гарантии (БГ)</option>
            </Select>
            {can_write && (
              <>
                <Button $size="sm" onClick={() => setShowAddModal((v) => !v)}>
                  + Добавить кредит / лизинг
                </Button>
                <Button $size="sm" $variant="secondary" onClick={() => setShowImport(true)}>
                  Загрузить из Excel
                </Button>
              </>
            )}
          </HeaderActions>
        </HeaderRow>

        {can_write && showAddModal && (
          <AddDebtModal
            instType={instType}
            setInstType={setInstType}
            contractNo={contractNo}
            setContractNo={setContractNo}
            creditor={creditor}
            setCreditor={setCreditor}
            title={title}
            setTitle={setTitle}
            totalLimit={totalLimit}
            setTotalLimit={setTotalLimit}
            principalBalance={principalBalance}
            setPrincipalBalance={setPrincipalBalance}
            rateType={rateType}
            setRateType={setRateType}
            baseRate={baseRate}
            setBaseRate={setBaseRate}
            marginRate={marginRate}
            setMarginRate={setMarginRate}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            leasedAssetName={leasedAssetName}
            setLeasedAssetName={setLeasedAssetName}
            leasedAssetCount={leasedAssetCount}
            setLeasedAssetCount={setLeasedAssetCount}
            collateral={collateral}
            setCollateral={setCollateral}
            covenants={covenants}
            setCovenants={setCovenants}
            autoGenerateSchedule={autoGenerateSchedule}
            setAutoGenerateSchedule={setAutoGenerateSchedule}
            isCreating={isCreating}
            errorMsg={errorMsg}
            onSubmit={handleCreateDebt}
            onClose={() => setShowAddModal(false)}
          />
        )}

        <DebtsKpiStrip
          totalCreditPrincipal={data.total_credit_principal}
          availableCreditLimit={data.available_credit_limit}
          totalLeasingPrincipal={data.total_leasing_principal}
          leasedUnitsTotal={data.leased_units_total}
          totalGuaranteesAmount={data.total_guarantees_amount}
          paymentsNext30d={data.payments_next_30d}
          paymentsNext7d={data.payments_next_7d}
        />
      </Section>

      <DebtsCalendarGrid calendarByMonth={data.calendar_by_month} />

      <DebtInstrumentsTable
        instruments={data.instruments}
        onSelectInstrument={setSelectedInstrumentId}
      />

      <UpcomingScheduleTable
        schedule={data.upcoming_schedule}
        can_write={can_write}
        onSelectInstrument={setSelectedInstrumentId}
        onMarkPaid={(itemId, amount) =>
          markPaid({
            item_id: itemId,
            paid_amount: amount,
          })
        }
      />

      {selectedInstrumentId && (
        <DebtDetailModal
          instrumentId={selectedInstrumentId}
          onClose={() => setSelectedInstrumentId(null)}
          editable={editable}
        />
      )}

      {can_write && (
        <DebtsImportDialog open={showImport} onClose={() => setShowImport(false)} />
      )}
    </>
  );
}
